import React, { useState, useEffect } from 'react';

interface SimpleScheduledPushProps {
  currentTheme: string;
}

interface ScheduleStatus {
  has_schedule: boolean;
  schedule?: {
    interval_minutes: number;
    message: string;
    enabled: boolean;
    last_sent: string | null;
    created_at: string | null;
  };
}

const SimpleScheduledPush: React.FC<SimpleScheduledPushProps> = ({ currentTheme }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ScheduleStatus | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(30);
  const [message, setMessage] = useState('现在是 {time}，该继续你的项目了！');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('请先登录');
        return;
      }

      const response = await fetch('/api/scheduled-push/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        setIsRunning(data.has_schedule && data.schedule?.enabled);
      } else {
        console.error('获取状态失败:', response.status);
      }
    } catch (error) {
      console.error('获取状态异常:', error);
    }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('请先登录');
        return;
      }

      const response = await fetch('/api/scheduled-push/start', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          interval_minutes: intervalMinutes,
          message: message
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setIsRunning(true);
        fetchStatus();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '启动定时推送失败');
      }
    } catch (error) {
      console.error('启动定时推送异常:', error);
      alert('启动定时推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('请先登录');
        return;
      }

      const response = await fetch('/api/scheduled-push/stop', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setIsRunning(false);
        setStatus(null);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '停止定时推送失败');
      }
    } catch (error) {
      console.error('停止定时推送异常:', error);
      alert('停止定时推送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('请先登录');
        return;
      }

      const response = await fetch('/api/scheduled-push/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '测试推送失败');
      }
    } catch (error) {
      console.error('测试推送异常:', error);
      alert('测试推送失败');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    maxWidth: '600px',
    margin: '20px auto',
    padding: '20px',
    backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#fff',
    border: `1px solid ${currentTheme === 'dark' ? '#434343' : '#d9d9d9'}`,
    borderRadius: '8px',
    color: currentTheme === 'dark' ? '#fff' : '#000'
  };

  const buttonStyle = {
    padding: '10px 20px',
    margin: '5px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'loading' ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#1890ff',
    color: '#fff'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ff4d4f',
    color: '#fff'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#52c41a',
    color: '#fff'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    margin: '8px 0',
    border: `1px solid ${currentTheme === 'dark' ? '#434343' : '#d9d9d9'}`,
    borderRadius: '4px',
    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#fff',
    color: currentTheme === 'dark' ? '#fff' : '#000'
  };

  return (
    <div style={containerStyle}>
      <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
        ⏰ 定时推送管理
      </h3>
      
      <p style={{ color: currentTheme === 'dark' ? '#ccc' : '#666', marginBottom: '20px' }}>
        真正的服务器端定时推送，即使应用在后台也能正常接收推送通知
      </p>

      {!isRunning ? (
        <div>
          <h4>启动定时推送</h4>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              推送间隔（分钟）:
            </label>
            <input
              type="number"
              value={intervalMinutes}
              onChange={(e) => setIntervalMinutes(Number(e.target.value))}
              min="1"
              max="1440"
              style={inputStyle}
              placeholder="30"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              推送消息:
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              style={inputStyle}
              placeholder="现在是 {time}，该继续你的项目了！"
              maxLength={200}
            />
          </div>

          <button
            onClick={handleStart}
            disabled={loading}
            style={primaryButtonStyle}
          >
            {loading ? '启动中...' : '🚀 启动定时推送'}
          </button>
        </div>
      ) : (
        <div>
          <h4>当前定时推送状态</h4>
          {status?.schedule && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#f5f5f5', borderRadius: '4px' }}>
              <p><strong>推送间隔：</strong>{status.schedule.interval_minutes} 分钟</p>
              <p><strong>推送消息：</strong>{status.schedule.message}</p>
              <p><strong>状态：</strong>
                <span style={{ color: status.schedule.enabled ? '#52c41a' : '#ff4d4f' }}>
                  {status.schedule.enabled ? '✅ 运行中' : '❌ 已暂停'}
                </span>
              </p>
              {status.schedule.last_sent && (
                <p><strong>最后推送：</strong>{new Date(status.schedule.last_sent).toLocaleString()}</p>
              )}
              {status.schedule.created_at && (
                <p><strong>创建时间：</strong>{new Date(status.schedule.created_at).toLocaleString()}</p>
              )}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleStop}
              disabled={loading}
              style={dangerButtonStyle}
            >
              {loading ? '停止中...' : '⏹️ 停止定时推送'}
            </button>
            
            <button
              onClick={handleTest}
              disabled={loading}
              style={secondaryButtonStyle}
            >
              {loading ? '测试中...' : '🧪 测试推送'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleScheduledPush;
